<script setup lang="ts">
    import { DebugMessage } from "@/components/DebugLog/types";
    import moment from "moment/moment";
    import emitter from "tiny-emitter/instance";
    import { onBeforeUnmount, onMounted, ref } from "vue";

    const messages = ref<DebugMessage[]>([]);
    const show = ref(false);
    const isDebug = ref(false);

    const addMessage = ({ message }: { message: DebugMessage }) => {
        const completeMessage = {
            ...message,
            time: message.time ?? moment().format("HH:mm:ss"),
            type: message.type ?? "info",
        };

        const modifiedMessages = [...messages.value];

        if (
            completeMessage.type === "info" &&
            modifiedMessages[0] &&
            modifiedMessages[0].type === "info" &&
            completeMessage.content === modifiedMessages[0].content &&
            completeMessage.source === modifiedMessages[0].source
        ) {
            modifiedMessages[0] = {
                ...completeMessage,
                repeated: true,
            };
        } else {
            modifiedMessages.push(completeMessage);
        }

        messages.value = modifiedMessages;
    };

    const onDebugClick = () => {
        show.value = !show.value;
    };

    const onHashChange = ({ message }: { message: DebugMessage }) => {
        addMessage({ message });
    };

    const onClear = () => {
        messages.value = [];
    };

    onMounted(() => {
        if (location?.hash?.includes?.("debug")) {
            isDebug.value = true;
        }

        window?.addEventListener?.("hashchange", (window) => {
            if (window.newURL.includes("debug") && !isDebug.value) {
                emitter.on("debug-log", onHashChange);
                isDebug.value = true;
            } else if (!window.newURL.includes("debug") && isDebug.value) {
                emitter.off("debug-log", onHashChange);
                isDebug.value = false;
                messages.value = [];
            }
        });
    });

    onBeforeUnmount(() => {
        emitter.off("debug-log", onHashChange);
    });
</script>

<template>
    <div v-if="isDebug" style="display: contents">
        <Button class="debug-button" type="button" @click="onDebugClick">Debug</Button>
        <div v-if="show" class="debug-messages">
            <Button type="button" @click="onClear">Clear</Button>
            <ul>
                <li v-for="(message, index) in messages" :key="index" :class="[message.type]">
                    {{
                        `${message.source} (${message.time}): ${message.content}${message.repeated ? " [repeat]" : ""}`
                    }}
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import "@/style/variables";

    .debug-button {
        position: absolute;
        top: 0;
        left: 0;
    }

    .debug-messages {
        position: absolute;
        top: 2rem;
        left: 0;
        bottom: 0;
        overflow: auto;
        width: 25rem;
        height: 100%;
        background-color: white;
        z-index: calc($z-index-max + 1);
        padding: 0.5rem;

        ul {
            list-style: none;
            margin: 0;
            padding: 0;

            li {
                &.success {
                    background-color: $color-dialog-success-background;
                }

                &.error {
                    background-color: $dialog-error;
                }
            }
        }
    }
</style>
